import React, { useState, useEffect } from 'react';
import { useQualityEvents } from '../../context/QualityEventsContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';

const InvestigationForm = ({ deviation, onInvestigationSaved, onCAPACreated }) => {
  const { saveInvestigation, assignCAPAFromDeviation } = useQualityEvents();
  const [formData, setFormData] = useState({
    problemStatement: '',
    whatHappened: '',
    impact: '',
    containment: '',
    rootCause: '',
    proposedFix: '',
    capaRequired: null
  });

  useEffect(() => {
    if (deviation?.investigation) {
      setFormData({
        problemStatement: deviation.investigation.problemStatement || '',
        whatHappened: deviation.investigation.whatHappened || '',
        impact: deviation.investigation.impact || '',
        containment: deviation.investigation.containment || '',
        rootCause: deviation.investigation.rootCause || '',
        proposedFix: deviation.investigation.proposedFix || '',
        capaRequired: deviation.investigation.capaRequired
      });
    }
  }, [deviation]);

  const handleSave = () => {
    saveInvestigation(deviation.id, formData);
    toast.success('Investigation saved');
    onInvestigationSaved?.();
  };

  const handleCreateCAPA = () => {
    if (!formData.rootCause || !formData.proposedFix) {
      toast.error('Please complete root cause and proposed fix before creating CAPA');
      return;
    }

    const capaDraft = {
      title: `CAPA for ${deviation.title}`,
      priority: deviation.severity === 'Critical' ? 'Critical' : deviation.severity === 'Major' ? 'High' : 'Medium',
      correctiveActions: [formData.proposedFix],
      preventiveActions: [],
      tasks: []
    };

    const newCAPA = assignCAPAFromDeviation(deviation.id, capaDraft);
    toast.success('CAPA created and linked', {
      description: `Created ${newCAPA.id}`
    });
    onCAPACreated?.(newCAPA);
  };

  if (!deviation?.investigation) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="problemStatement">Problem Statement</Label>
        <Textarea
          id="problemStatement"
          value={formData.problemStatement}
          onChange={(e) => setFormData({ ...formData, problemStatement: e.target.value })}
          placeholder="Brief statement of the problem"
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="whatHappened">What Happened</Label>
        <Textarea
          id="whatHappened"
          value={formData.whatHappened}
          onChange={(e) => setFormData({ ...formData, whatHappened: e.target.value })}
          placeholder="Describe what happened in plain English"
          rows={4}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="impact">Impact Assessment</Label>
        <Textarea
          id="impact"
          value={formData.impact}
          onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
          placeholder="Describe the impact on batch/line/system"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="containment">Containment Action</Label>
        <Textarea
          id="containment"
          value={formData.containment}
          onChange={(e) => setFormData({ ...formData, containment: e.target.value })}
          placeholder="What was done immediately to contain the issue"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="rootCause">Root Cause</Label>
        <Textarea
          id="rootCause"
          value={formData.rootCause}
          onChange={(e) => setFormData({ ...formData, rootCause: e.target.value })}
          placeholder="Root cause in plain English (no jargon)"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="proposedFix">Proposed Fix</Label>
        <Textarea
          id="proposedFix"
          value={formData.proposedFix}
          onChange={(e) => setFormData({ ...formData, proposedFix: e.target.value })}
          placeholder="Proposed corrective/preventive action in plain English"
          rows={3}
        />
      </div>

      <Separator />

      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="capaRequired">CAPA Required?</Label>
          <p className="text-xs text-slate-500">Toggle to indicate if CAPA is needed</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="capaRequired"
              checked={formData.capaRequired === true}
              onCheckedChange={(checked) => setFormData({ ...formData, capaRequired: checked ? true : null })}
            />
            <Label htmlFor="capaRequired" className="cursor-pointer">Yes</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="capaNotRequired"
              checked={formData.capaRequired === false}
              onCheckedChange={(checked) => setFormData({ ...formData, capaRequired: checked ? false : null })}
            />
            <Label htmlFor="capaNotRequired" className="cursor-pointer">No</Label>
          </div>
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button
          variant="outline"
          onClick={handleSave}
          className="flex-1"
        >
          Save Investigation
        </Button>
        {formData.capaRequired === true && (
          <Button
            className="bg-[#fd4816] hover:bg-[#ea380c] text-white flex-1"
            onClick={handleCreateCAPA}
          >
            Create CAPA
          </Button>
        )}
      </div>
    </div>
  );
};

export default InvestigationForm;

